<template>
  <div class="job-filter-popup" role="dialog" ref="root" :class="{ 'scrolled-to-end': hasScrolledBodyToEnd }">
    <header class="site-header-container is-active">
      <h3 class="new-heading-3 mb-0">{{ dictionary.filterPopupTitle }}</h3>
      <button
        ref="closeButton"
        aria-expanded="true"
        aria-controls="job-filter"
        class="site-menu-toggle"
        data-dm="job-filter.popup-close"
        @click="onClose"
      >
        <Hamburger />
      </button>
    </header>
    <main class="job-filter-popup-body custom-scrollbar" ref="scrollBody">
      <div v-for="(facets, group) in filters" :key="group">
        <p class="text-caption-caps">{{ dictionary.filterGroupHeadings[group] }}</p>
        <div class="button-group mb-7">
          <TogglePill
            v-for="pill in facets"
            :label="pill" small dark
            :close="false"
            :active="isActiveFilter(group, pill)"
            :disabled="filterReturnsNoResults(group, pill)"
            :key="pill"
            :data-dm-type="group"
            @toggle="toggleFilter(group, pill)"
          />
        </div>
      </div>
    </main>
    <footer class="job-filter-popup-footer">
      <button class="text-link button-unstyled" @click="onClear">
        {{ dictionary.filterPopupClearLabel }}
      </button>
      <button class="button button-primary button-small" :disabled="!liveResults.length" @click="onConfirm">
        {{ dictionary.filterPopupConfirmLabel.replace('{numberOfResults}', liveResults.length) }}
      </button>
    </footer>
  </div>
</template>

<script setup>
import {
  computed, onMounted, ref, watch,
} from 'vue';
import TogglePill from '@/components/filter/TogglePill.vue';
import Hamburger from '@/components/Hamburger.vue';
import { scrollEndChangeHandler } from '@/utils/intersection';
import { deepCopy } from '@/utils/object';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  filterItems: {
    type: Function,
    default: () => [],
  },
  activeFilters: {
    type: Object,
    default: () => ({}),
  },
  dictionary: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['confirm', 'close']);

const root = ref(null);
const closeButton = ref(null);
const scrollBody = ref(null);
const hasScrolledBodyToEnd = ref(false);
const selectedFilters = ref({});

defineExpose({ root, closeButton });

onMounted(() => {
  scrollEndChangeHandler(scrollBody.value, (scrolledToEnd) => {
    hasScrolledBodyToEnd.value = scrolledToEnd;
  });
});

watch(() => props.activeFilters, (newActiveFilters) => {
  // set them explicitly to remove reactive reference
  setSelectedFilters(newActiveFilters);
});

const liveResults = computed(() => props.filterItems(selectedFilters.value));

function onConfirm() {
  // deep copy to remove the reference and prevent updating the parent filter data
  emit('confirm', deepCopy(selectedFilters.value));
  onClose();
}

function onClear() {
  selectedFilters.value = {};
}

function onClose() {
  emit('close');
  // reset filters back to the ones that were active before opening
  setSelectedFilters(props.activeFilters);
}

function setSelectedFilters(filters) {
  selectedFilters.value = deepCopy(filters);
}

function filterReturnsNoResults(groupKey, facet) {
  if (isActiveFilter(groupKey, facet)) {
    return false;
  }

  // single filter on its own does not return results
  const noFilterResults = !props.filterItems({ [groupKey]: [facet] }).length;
  if (noFilterResults) {
    return true;
  }

  // when this filter is applied on top of the current filters it does not return results
  const newFilterSelection = deepCopy(selectedFilters.value);
  const currentGroupValue = newFilterSelection[groupKey] || [];
  newFilterSelection[groupKey] = [...currentGroupValue, facet];
  const noFutureFilterResults = !props.filterItems(newFilterSelection).length;
  return noFutureFilterResults;
}

function toggleFilter(groupKey, facet) {
  const currentGroupValue = selectedFilters.value[groupKey] || [];
  if (isActiveFilter(groupKey, facet)) {
    const index = currentGroupValue.findIndex((_facet) => _facet === facet);
    // delete filter
    selectedFilters.value[groupKey].splice(index, 1);
    if (!currentGroupValue.length) {
      // delete/clean up filter group
      delete selectedFilters.value[groupKey];
    }
  } else {
    // add filter
    selectedFilters.value[groupKey] = [...currentGroupValue, facet];
  }
}

function isActiveFilter(groupKey, facet) {
  return selectedFilters.value[groupKey]?.includes(facet);
}
</script>
